/**
 * Native push registration (iOS APNs via Capacitor).
 *
 * The web app uses the Web Push API (see useNotifications.js), but Web Push does
 * not work inside the installed iOS app — that requires APNs. This module
 * handles the native path: request permission, register with APNs, and send the
 * resulting device token to our backend.
 *
 * It is safe to import everywhere: every entry point first checks
 * `isNativePlatform()` and no-ops on the web, so the Capacitor plugin is only
 * exercised on a real device.
 *
 * NOTE: delivery still requires a server-side APNs sender keyed with an APNs
 * auth key from your Apple Developer account. Until that exists, tokens are
 * stored but no native push is sent. See docs/APP_STORE_LAUNCH.md (Phase 2).
 */

import { Capacitor } from "@capacitor/core";
import { supabase } from "./supabase";
import { captureError } from "./monitoring";

/** True only inside the installed native app (not the browser / PWA). */
export function isNativePlatform() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Send the APNs device token to the backend, tagged with its platform. */
async function saveDeviceToken(token) {
  const headers = { "Content-Type": "application/json" };
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;

  await fetch(`${import.meta.env.VITE_API_URL || ""}/api/register-device`, {
    method: "POST",
    headers,
    body: JSON.stringify({ token, platform: Capacitor.getPlatform() }),
  });
}

let listenersAttached = false;

/**
 * Request permission and register for native push.
 * Returns true if permission was granted and registration started.
 * No-op (returns false) on the web.
 */
export async function registerNativePush() {
  if (!isNativePlatform()) return false;

  // Lazy-load so the plugin is never pulled into the web bundle's critical path.
  const { PushNotifications } = await import("@capacitor/push-notifications");

  if (!listenersAttached) {
    listenersAttached = true;

    PushNotifications.addListener("registration", (token) => {
      saveDeviceToken(token.value).catch((err) =>
        captureError(err, { where: "native-push:saveDeviceToken" })
      );
    });

    PushNotifications.addListener("registrationError", (err) => {
      captureError(new Error(err?.error || "APNs registration error"), {
        where: "native-push:registrationError",
      });
    });
  }

  try {
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return false;
    await PushNotifications.register();
    return true;
  } catch (err) {
    captureError(err, { where: "native-push:register" });
    return false;
  }
}
